import React, { FC, ReactNode } from 'react'
import { RouteComponentProps, withRouter } from 'react-router-dom'

import { Content } from '@acorns/web-components'
import { compose } from 'recompose'
import styled, { css } from 'styled-components'

import { DisclosuresFooter } from 'components/SidebarFrame/DisclosuresFooter'
import { Footer as BasicFooter } from 'components/SidebarFrame/Footer'
import { CUSTOM_SMALL_DESKTOP } from 'router/navigation/components/styled-components'
import { useShowNavBar, useShowSideNav } from 'router/navigation/hooks'
import NavigationWrapper from 'router/navigation/NavigationWrapper'
import { routes } from 'router/routes'
import theme from 'theme'
import {
  BANKING_HUB_DISCLOSURE_CONTENT,
  CHECKING_HOME_DISCLOSURE_CONTENT,
  EMERGENCY_FUND_DISCLOSURE_CONTENT,
  HOME_DISCLOSURE_CONTENT,
  INVESTING_HUB_DISCLOSURE_CONTENT,
} from 'utils/testing/testIDs'

import { EmptyNav, SimpleNavbar } from '../Navbar'
import {
  BankingHubDisclosureContent,
  CheckingHomeDisclosureContent,
  HomePageDisclosureContent,
  InvestingHubDisclosureContent,
} from './DisclosureContent'

type Props = {
  children: ReactNode
  className?: string
  renderNav?: any
  logoLink?: string
  navTitle?: string
  hideFooter?: boolean
}

type AddedProps = {
  showSideNav: boolean
  forceHideHav: (page: string) => void
  setHideFooter: (page: string) => void
  setShowSideNav: (newVal: boolean) => void
  toggles: any
  renderNav: boolean
}

type EnhancedProps = Props & AddedProps & RouteComponentProps<any>

const Container = styled.div<{
  showSideBottomNav: boolean
}>`
  background: ${theme.colors.white};
  min-height: 100%;
  width: 100%;

  ${(props) =>
    props.showSideBottomNav &&
    css`
      padding-left: 320px;
      @media (max-width: ${CUSTOM_SMALL_DESKTOP}px) {
        padding-left: 230px;
      }
      @media screen and (max-width: ${(props) =>
          props.theme.breakpoints.md}px) {
        padding-left: 94px;
      }
      @media screen and (max-width: ${(props) =>
          props.theme.breakpoints.sm}px) {
        padding-left: 0px;
      }
    `}

  @media screen and (max-width: ${theme.breakpoints.sm}px) {
    width: 100%;
    background: ${theme.colors.ivory};
  }
`

const Body = styled.div<{
  showSideBottomNav: boolean
}>`
  min-height: calc(100vh - 174px);
  ${(props) =>
    props.showSideBottomNav &&
    css`
      width: 100%;
      padding: 0px 30px;
      @media screen and (max-width: ${(props) =>
          props.theme.breakpoints.sm}px) {
        padding: 0px;
      }
    `}
  @media screen and (max-width: ${theme.breakpoints.md}px) {
    min-height: calc(100vh - 94px);
  }
`

export const FooterContainer = styled.div`
  @media screen and (min-width: ${theme.breakpoints.md + 1}px) {
    width: 100%;
  }
`

const Footer = styled.div<{
  isNotDisplayed: boolean
}>`
  align-items: center;
  display: flex;
  justify-content: center;
  padding: 50px 60px;

  @media screen and (max-width: ${theme.breakpoints.md}px) {
    padding: 30px 40px;
    background: ${theme.colors.white};
    margin-bottom: 10px;
  }

  ${(props) => props.isNotDisplayed && 'display: none;'}
`

const StyledContent = styled(Content)<{
  showSideBottomNav: boolean
  isRecap: boolean
}>`
  ${(props) =>
    props.isRecap &&
    css`
      max-width: initial;
      padding: 0;
    `}
  ${(props) =>
    props.showSideBottomNav &&
    css`
      max-width: 1200px;
      padding: 0;
      @media screen and (max-width: ${theme.breakpoints.md}px) {
        padding: 0;
      }
    `}
  @media screen and (max-width: ${theme.breakpoints.sm}px) {
    padding: 0;
  }
`

const pathsWithHiddenFooter = [
  'invite',
  'offer-details',
  'age-of-transfer-reached',
  'age-of-transfer-next-steps',
  'age-of-transfer-faq',
  'portfolio',
  'performance',
  'passions',
  'referral-status',
  'learn',
  'money-manager',
  'invest/core/tips',
  'settings/change-password',
  'settings/mfa',
  'settings/personal/email',
  'settings/personal/phone',
  'settings/my-subscription/upgrade-lander',
  'benefits-hub/tax-filing',
]

const enhance = compose<EnhancedProps, Props>(withRouter)

type FooterContentProps = {
  isHomePage: boolean
  isInvestingHubPage: boolean
  isCheckingHome: boolean
  isNotDisplayed: boolean
  isBankingHub: boolean
  isEmergencyFund: boolean
}

const FooterContent = ({
  isCheckingHome,
  isHomePage,
  isInvestingHubPage,
  isNotDisplayed,
  isBankingHub,
  isEmergencyFund,
}: FooterContentProps) => {
  if (isHomePage) {
    return (
      <DisclosuresFooter
        testId={HOME_DISCLOSURE_CONTENT}
        content={HomePageDisclosureContent}
      />
    )
  }
  if (isInvestingHubPage) {
    return (
      <DisclosuresFooter
        testId={INVESTING_HUB_DISCLOSURE_CONTENT}
        content={InvestingHubDisclosureContent}
      />
    )
  }
  if (isCheckingHome) {
    return (
      <DisclosuresFooter
        testId={CHECKING_HOME_DISCLOSURE_CONTENT}
        content={CheckingHomeDisclosureContent}
      />
    )
  }
  if (isBankingHub || isEmergencyFund) {
    return (
      <DisclosuresFooter
        testId={
          isBankingHub
            ? BANKING_HUB_DISCLOSURE_CONTENT
            : EMERGENCY_FUND_DISCLOSURE_CONTENT
        }
        content={BankingHubDisclosureContent}
      />
    )
  }
  return (
    <Footer isNotDisplayed={isNotDisplayed}>
      <BasicFooter />
    </Footer>
  )
}

type AddedSidebarFrameProps = {
  showNavOnThisRoute: boolean
  showNavBar: boolean
  isHomePage: boolean
  isInvestingHubPage: boolean
  isCheckingHome: boolean
  isBankingHub: boolean
  isEmergencyFund: boolean
  footerNotDisplayed: boolean
  isRecap: boolean
  children?: React.ReactNode
}

type SidebarFrameProps = Props &
  AddedSidebarFrameProps &
  RouteComponentProps<any>

const RenderNavBar = (props: SidebarFrameProps) => {
  if (props.location.pathname.includes(routes.portfolio.esgEducationalTour)) {
    return (
      <SimpleNavbar
        path={routes.portfolio.changePortfolioTheme}
        search={props.location?.search}
      />
    )
  }

  if (props.showNavBar) {
    return <EmptyNav />
  }

  return null
}

const SidebarFrameContents = (props: SidebarFrameProps) => {
  return (
    <Container
      showSideBottomNav={props.showNavOnThisRoute}
      className={props.className}
    >
      <Body showSideBottomNav={props.showNavOnThisRoute}>
        <RenderNavBar {...props} />
        <StyledContent
          showSideBottomNav={props.showNavOnThisRoute}
          isRecap={props.isRecap}
        >
          {props.children}
        </StyledContent>
      </Body>
      <FooterContainer>
        <FooterContent
          isHomePage={props.isHomePage}
          isInvestingHubPage={props.isInvestingHubPage}
          isCheckingHome={props.isCheckingHome}
          isNotDisplayed={props.hideFooter ?? props.footerNotDisplayed}
          isBankingHub={props.isBankingHub}
          isEmergencyFund={props.isEmergencyFund}
        />
      </FooterContainer>
    </Container>
  )
}

const SidebarFrame: FC<EnhancedProps> = (props) => {
  const isHomePage = props.location.pathname.includes(routes.presentHome)
  const isInvestingHubPage =
    props.location.pathname === routes.investingHub.index
  const isCheckingHome = props.location.pathname === routes.spend.index
  const isBankingHub = props.location.pathname === routes.bankingHub.index
  const isEmergencyFund =
    props.location.pathname === routes.emergencyFund.index ||
    props.location.pathname === routes.emergencyFund.settings
  const isRecap =
    props.location.pathname.includes(routes.publishing.recap.custodian.base) &&
    props.location.pathname.search('/setup') < 0
  const { showNavOnThisRoute } = useShowSideNav()
  const { showNavBar } = useShowNavBar()

  const footerNotDisplayed = pathsWithHiddenFooter.some((path) =>
    props.location.pathname.includes(path),
  )
  const sidebarFrameContentProps = {
    footerNotDisplayed,
    showNavOnThisRoute,
    showNavBar,
    isHomePage,
    isInvestingHubPage,
    isRecap,
    isCheckingHome,
    isBankingHub,
    isEmergencyFund,
  }

  return (
    <NavigationWrapper>
      <SidebarFrameContents {...sidebarFrameContentProps} {...props} />
    </NavigationWrapper>
  )
}

export default enhance(SidebarFrame)
